import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useUser } from '../hooks/useUser.ts';
import { apiFetch } from '../lib/api.ts';

const SIDEBAR_KEY = 'redawn:sidebar-collapsed';

export function Layout() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Remembered across navigations and sessions: someone working through a wide
  // table collapses it once, not on every page.
  const [collapsed, setCollapsed] = useState(
    () => globalThis.localStorage?.getItem(SIDEBAR_KEY) === 'true',
  );
  useEffect(() => {
    globalThis.localStorage?.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  async function logout() {
    try {
      await apiFetch<null>('/api/auth/logout', { method: 'POST' });
    } catch {
      // Best-effort: server might be unreachable. Clear client state anyway.
    }
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  /**
   * Nav is driven by the permission matrix, not by role branches. Each link
   * appears iff the user holds the permission the route and API also gate on,
   * so the three can't drift apart as they previously did.
   *
   * A session predating permissions (no `permissions` field) shows everything;
   * the routes and the server still gate, so nothing is exposed by that.
   */
  const perms = (user?.permissions as string[] | undefined) ?? null;
  const can = (key: string) => (perms ? perms.includes(key) : true);
  const canAny = (...keys: string[]) => keys.some(can);

  const SECTIONS: Array<{ title: string; links: Array<{ to: string; label: string; perm: string; end?: boolean }> }> = [
    { title: 'Analytics', links: [
      { to: '/', label: 'Overview', perm: 'overview.view', end: true },
      { to: '/customers', label: 'Customers', perm: 'customers.view' },
      { to: '/suppliers', label: 'Suppliers', perm: 'suppliers.view' },
      { to: '/skus', label: 'SKUs', perm: 'skus.view' },
      { to: '/inventory', label: 'Inventory', perm: 'inventory.view' },
      { to: '/stock-counts', label: 'Stock counts', perm: 'stock_counts.view' },
    ]},
    { title: 'Sales', links: [
      { to: '/invoices', label: 'Invoices', perm: 'invoices.view' },
      { to: '/ar-aging', label: 'AR aging', perm: 'ar_aging.view' },
      { to: '/sales-performance', label: 'Salesperson', perm: 'sales_performance.view' },
      { to: '/sales-orders', label: 'Sales orders', perm: 'sales_orders.view' },
      { to: '/cash-sales', label: 'Cash sales', perm: 'cash_sales.view' },
      { to: '/delivery-orders', label: 'Delivery orders', perm: 'delivery_orders.view' },
      { to: '/sales-returns', label: 'Sales returns', perm: 'sales_returns.view' },
    ]},
    { title: 'Purchases', links: [
      { to: '/purchase-orders', label: 'Purchase orders', perm: 'purchase_orders.view' },
      { to: '/receiving', label: 'Receiving', perm: 'purchase_orders.receive' },
      { to: '/purchases', label: 'Purchases (GRN)', perm: 'purchases.view' },
    ]},
    { title: 'Operations', links: [
      { to: '/operations', label: 'RFID activity', perm: 'operations.view' },
      { to: '/dispatch', label: 'Dispatch', perm: 'delivery_orders.dispatch' },
      { to: '/dispatch-exceptions', label: 'Dispatch exceptions', perm: 'operations.view' },
      { to: '/admin/rfid-exclusions', label: 'RFID exclusions', perm: 'admin.rfid_exclusions' },
      { to: '/admin/write-tasks', label: 'Write tasks', perm: 'write_tasks.view' },
    ]},
    { title: 'Admin', links: [
      { to: '/admin', label: 'Users', perm: 'admin.users', end: true },
      { to: '/admin/permissions', label: 'Role access', perm: 'admin.permissions' },
      { to: '/admin/sync-state', label: 'Sync state', perm: 'admin.sync_state' },
      { to: '/admin/alerts', label: 'Alerts', perm: 'admin.alerts' },
      { to: '/admin/vendor-log', label: 'Vendor calls', perm: 'admin.vendor_log' },
      { to: '/admin/audit-log', label: 'Audit log', perm: 'admin.audit_log' },
    ]},
  ];

  return (
    <div className={`layout${collapsed ? ' layout-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-brand">
          REDAWN
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setCollapsed(true)}
            title="Hide the menu"
            aria-label="Hide the menu"
          >
            «
          </button>
        </div>

        {SECTIONS.filter((sec) => canAny(...sec.links.map((l) => l.perm))).map((sec) => (
          <div key={sec.title}>
            <div className="sidebar-section">{sec.title}</div>
            <nav>
              {sec.links.filter((l) => can(l.perm)).map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end}>{l.label}</NavLink>
              ))}
            </nav>
          </div>
        ))}

        {user && (
          <div className="user-menu">
            <div className="user-name">{user.name}</div>
            <div className="user-role mono">
              {user.email} · <span style={{ color: 'var(--accent)' }}>{user.role}</span>
            </div>
            <NavLink to="/account" className="account-link">
              Change password
            </NavLink>
            <button onClick={logout} className="logout-btn">
              Sign out
            </button>
          </div>
        )}
      </aside>
      {collapsed && (
        <button
          type="button"
          className="sidebar-show"
          onClick={() => setCollapsed(false)}
          title="Show the menu"
          aria-label="Show the menu"
        >
          »
        </button>
      )}
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
